/*
  A line told in words (2.4.5): a library mapping line, a standards line, a required-data setting, an "at least
  one" setting, a pick-list value, a symbol, a line style. The screens show the line with its own marks.
*/

import type { LineRow } from "../../../api/types";
import { MATCH_WORDS, lineKindWord } from "./words";
import { textOf } from "./text";

export interface LineWords {
  /** What the line is, in a few words. */
  title: string;
  /** Short facts under the title, each one a plain sentence or phrase. */
  facts: string[];
  /** Flags that matter to the reader ("Library not loaded yet"). */
  flags: string[];
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
}

export function describeLine(line: Pick<LineRow, "kind" | "content" | "code">): LineWords {
  const c = record(line.content);
  switch (line.kind) {
    case "mapping":
    case "standards": {
      const library = textOf(c.library);
      const label = textOf(c.object_label) || textOf(c.object_id);
      const predicate = textOf(c.predicate);
      const word = (MATCH_WORDS as Record<string, string>)[predicate] ?? predicate.replace(/_/g, " ");
      const noMatch = predicate === "no_match";
      const facts: string[] = [];
      if (noMatch) {
        if (textOf(c.no_match_reason)) facts.push(`Why: ${textOf(c.no_match_reason)}`);
        if (textOf(c.words_searched)) facts.push(`Words searched: ${textOf(c.words_searched)}`);
        const searched = stringList(c.libraries_searched);
        if (searched.length > 0) facts.push(`Libraries searched: ${searched.join(", ")}`);
      } else if (textOf(c.library_version)) {
        facts.push(`${library} ${textOf(c.library_version)}`);
      }
      if (textOf(c.library_classes)) facts.push(`Classes the property is assigned to: ${textOf(c.library_classes)}`);
      if (textOf(c.comment)) facts.push(textOf(c.comment));
      if (textOf(c.case_code)) facts.push(`For the case ${textOf(c.case_code)}`);
      if (textOf(c.exchange_note)) facts.push(textOf(c.exchange_note));
      // a line about one part of its element: a value of the pick list, or a link slot of the kind of document (CP-15)
      if (line.code) facts.unshift(`About ${line.code}`);
      const flags: string[] = [];
      // the stored CFIHOS classes say WARNING when the property is not assigned to the class of the item (the check note of DC-12)
      if (/\bWARNING\b/.test(textOf(c.library_classes))) flags.push("CFIHOS property not for this class");
      if (c.library_not_loaded === true) flags.push("Library not loaded yet");
      if (c.not_searched_by_meaning_yet === true) flags.push("Not searched by meaning yet");
      if (c.unchecked === true) flags.push("Unchecked");
      if (c.derived_not_drawn === true) flags.push("Made by the app");
      const half = textOf(c.half);
      const where = [library, half ? `(${half === "pid" ? "P&ID" : "PFD"} half)` : ""].filter(Boolean).join(" ");
      return { title: noMatch ? `${where}: no match` : `${where}: ${word.toLowerCase()} ${label}`.trim(), facts, flags };
    }
    case "phase_setting": {
      const perCase = record(c.per_case);
      const cases = Object.entries(perCase).map(([name, phase]) => `${name}: ${textOf(phase)}`);
      const title = cases.length > 0 ? `Required from, by case` : `Required from ${textOf(c.phase)}`;
      return { title, facts: cases, flags: [] };
    }
    case "at_least_one": {
      const facts: string[] = [];
      if (textOf(c.line_kind_code)) facts.push(`Kind: ${textOf(c.line_kind_code)}`);
      if (textOf(c.line_phase)) facts.push(`Line from ${textOf(c.line_phase)}`);
      return { title: c.phase ? `At least one from ${textOf(c.phase)}` : "At least one", facts, flags: [] };
    }
    case "pick_value": {
      const facts: string[] = [];
      if (textOf(c.description)) facts.push(textOf(c.description));
      const size = record(c.size);
      if (textOf(size.counterpart_code)) {
        facts.push(`${textOf(size.system)} corresponds to ${textOf(size.counterpart_code)} by ${textOf(size.source_table)}`.trim());
      }
      const exchange = record(c.exchange);
      if (textOf(exchange.library_code)) facts.push(`Library code ${textOf(exchange.library_code)}${textOf(exchange.library_text) ? `: ${textOf(exchange.library_text)}` : ""}`);
      if (textOf(c.value_source)) facts.push(`Comes from: ${textOf(c.value_source)}`);
      const title = textOf(c.title) || textOf(c.code);
      return { title: textOf(c.code) && textOf(c.code) !== title ? `${title} (${textOf(c.code)})` : title, facts, flags: [] };
    }
    case "symbol": {
      const facts: string[] = [];
      const by = record(c.chosen_by);
      if (textOf(by.value_code)) facts.push(`Chosen when the field value is ${textOf(by.value_code)}`);
      return { title: `Symbol ${textOf(c.symbol_code)}`, facts, flags: [] };
    }
    case "line_style": {
      const facts: string[] = [];
      const def = record(c.definition);
      const drawn = [textOf(def.stroke), textOf(def.width), textOf(def.dash), textOf(def.colour)].filter(Boolean);
      if (drawn.length > 0) facts.push(drawn.join(", "));
      const by = record(c.chosen_by);
      if (textOf(by.value_code)) facts.push(`Chosen when the field value is ${textOf(by.value_code)}`);
      return { title: `Line style ${textOf(c.style_code)}`, facts, flags: [] };
    }
    default:
      return { title: lineKindWord(String(line.kind)), facts: [], flags: [] };
  }
}

/** Lines in the order they are shown: library lines first, then settings, values, symbols. */
const KIND_ORDER = ["mapping", "standards", "phase_setting", "at_least_one", "pick_value", "symbol", "line_style"];

export function groupLines(lines: readonly LineRow[]): Array<{ kind: string; lines: LineRow[] }> {
  const groups = new Map<string, LineRow[]>();
  for (const line of lines) {
    const list = groups.get(line.kind) ?? [];
    list.push(line);
    groups.set(line.kind, list);
  }
  const known = KIND_ORDER.filter((kind) => groups.has(kind));
  const others = [...groups.keys()].filter((kind) => !KIND_ORDER.includes(kind));
  return [...known, ...others].map((kind) => ({ kind, lines: groups.get(kind) ?? [] }));
}
